import { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5radar from "@amcharts/amcharts5/radar";
import * as am5xy from "@amcharts/amcharts5/xy";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting.js";
import { useTheme } from "@mui/material";
import { MyTheme } from "./../colors";

export const NightingaleChart = ({ chartData, startDate, endDate, maximize }) => {
    const chartDivId = useRef(`nightingalechart-${Math.random().toString(36).substring(2, 11)}`);
    const rootRef = useRef(null);
    const theme = useTheme();
    const graphicTitle = chartData.title;

    useEffect(() => {
        if (rootRef.current) {
            rootRef.current.dispose();
            rootRef.current = null;
        }

        const generateChart = () => {
            try {
                let newChart = null;
                const startMs = new Date(startDate).getTime();
                const endMs = new Date(endDate).getTime();
                const endDatePlusOneDay = new Date(endMs);
                endDatePlusOneDay.setDate(endDatePlusOneDay.getDate() + 1);

                // --- 1. Crear root y aplicar tema de colores ---
                const root = am5.Root.new(chartDivId.current);
                rootRef.current = root;
                root.setThemes([MyTheme.new(root)]);

                // --- 2. Crear RadarChart (base del Nightingale) ---
                // Un Nightingale Chart es un gráfico polar donde cada columna
                // representa una categoría y su altura (radio) representa el valor.
                newChart = root.container.children.push(
                    am5radar.RadarChart.new(root, {
                        panX: false,
                        panY: false,
                        wheelX: "none",
                        wheelY: "none",
                        innerRadius: am5.percent(20),
                        layout: root.verticalLayout,
                    })
                );

                // --- 3. Crear eje X (categorías, circular) ---
                // Este eje rodea el chart y muestra las etiquetas de cada categoría
                const xRenderer = am5radar.AxisRendererCircular.new(root, {
                    minGridDistance: 30,
                });
                xRenderer.labels.template.setAll({
                    fontSize: 12,
                    textType: "radial",
                    radius: 10,
                    centerY: am5.p50,
                    oversizedBehavior: "truncate",
                    maxWidth: 100,
                });

                const xAxis = newChart.xAxes.push(
                    am5xy.CategoryAxis.new(root, {
                        maxDeviation: 0,
                        categoryField: "category",
                        renderer: xRenderer,
                        tooltip: am5.Tooltip.new(root, {}),
                    })
                );

                // --- 4. Procesar datos dinámicos desde chartData ---
                // Mismo patrón que DonutChart: filtra por rango de fechas
                // chartData.data es un array donde cada item tiene:
                //   - label: nombre de la categoría
                //   - data[0]: array de valores
                //   - data[1]: array de días desde hoy (para calcular la fecha)
                const processedData = chartData.data
                    .map((item) => {
                        const filteredValues = item.data[0].filter((value, index) => {
                            const date = new Date(
                                Date.now() - parseInt(item.data[1][index]) * 24 * 60 * 60 * 1000
                            ).getTime();
                            if (startMs === 0 && endMs === 0) {
                                return true;
                            } else {
                                return date >= startMs && date <= endDatePlusOneDay;
                            }
                        });
                        const count = filteredValues.filter((value) => value > 0).length;
                        return count > 0 ? { category: item.label, value: count } : null;
                    })
                    .filter(Boolean);

                // --- 5. Crear eje Y (valores, radial) ---
                // Se usa totalSum (o la mitad si el max no supera el 50%) para vision porcentual
                const totalSum = processedData.reduce((sum, item) => sum + item.value, 0);
                const maxValue = processedData.length > 0
                    ? Math.max(...processedData.map((item) => item.value))
                    : 0;
                const yMax = maxValue <= totalSum * 0.5 ? totalSum / 2 : totalSum;

                const yRenderer = am5radar.AxisRendererRadial.new(root, {});
                yRenderer.labels.template.setAll({
                    visible: false,
                });

                const yAxis = newChart.yAxes.push(
                    am5xy.ValueAxis.new(root, {
                        min: 0,
                        max: yMax,
                        strictMinMax: true,
                        renderer: yRenderer,
                    })
                );

                // --- 6. Crear la serie de columnas radiales ---
                // Cada columna va del centro hacia afuera con altura proporcional al valor
                const series = newChart.series.push(
                    am5radar.RadarColumnSeries.new(root, {
                        name: "Series",
                        xAxis: xAxis,
                        yAxis: yAxis,
                        valueYField: "value",
                        categoryXField: "category",
                    })
                );

                // Estilo de las columnas: bordes redondeados y colores por categoría
                series.columns.template.setAll({
                    cornerRadius: 5,
                    strokeOpacity: 0,
                    fillOpacity: 0.8,
                    width: am5.percent(97),
                    tooltipText: "{category}: {valueY}",
                });

                // Asignar un color diferente a cada columna usando el ColorSet del tema
                series.columns.template.adapters.add("fill", function (fill, target) {
                    const index = series.columns.indexOf(target);
                    if (index < 0) return fill; // Guard: si no encuentra la columna, mantiene color por defecto
                    return newChart.get("colors").getIndex(index);
                });

                // --- 7. Setear datos en los ejes y la serie ---
                xAxis.data.setAll(processedData);
                series.data.setAll(processedData);

                // --- 8. Leyenda ---
                const legend = newChart.children.push(
                    am5.Legend.new(root, {
                        centerX: am5.p50,
                        x: am5.p50,
                        marginTop: 15,
                    })
                );
                legend.data.setAll(series.dataItems);

                // --- 9. Título oculto (visible solo al exportar PDF) ---
                const title = newChart.children.push(
                    am5.Label.new(root, {
                        text: graphicTitle,
                        fontSize: 20,
                        x: am5.percent(50),
                        y: am5.percent(0),
                        centerX: am5.percent(50),
                        visible: false,
                    })
                );

                // --- 10. Exportación (PNG, PDF, etc.) ---
                const exporting = am5plugins_exporting.Exporting.new(root, {
                    menu: am5plugins_exporting.ExportingMenu.new(root, {
                        align: "right",
                        valign: "bottom",
                    }),
                    pdfOptions: {
                        addURL: false,
                    },
                });

                exporting.events.on("exportstarted", function (event) {
                    if (event.format === "pdf") {
                        title.set("visible", true);
                    }
                });

                exporting.events.on("exportfinished", function (event) {
                    title.set("visible", false);
                });

                // --- 11. Animación de entrada ---
                series.appear(1000);
                newChart.appear(1000, 100);

            } catch (error) {
                console.error("Error al cargar las bibliotecas de AmCharts:", error);
            }
        };

        generateChart();

        // --- 12. Cleanup: destruir instancia al desmontar ---
        return () => {
            if (rootRef.current) {
                rootRef.current.dispose();
                rootRef.current = null;
            }
        };
    }, [chartData, startDate, endDate]);

    return <div id={chartDivId.current} style={{ width: "100%", height: "500px" }}></div>;
};

export default NightingaleChart;
